import Papa from "papaparse"

/** Same pattern as InviteClients.jsx (RFC 5322–style). */
const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export function isValidInviteEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim())
}

export type ParsedInviteClient = {
  email: string
  firstName: string
  lastName: string
  phone: string
  clientType: "invited"
  invitedFrom: string
  notes?: string
}

type ColumnLayout = "email_first" | "name_first"

/** True when the cell looks like a phone number (digits present; not an email). */
export function isValidInvitePhone(phone: string): boolean {
  const trimmed = phone.trim()
  if (!trimmed || isValidInviteEmail(trimmed)) return false
  const digits = trimmed.replace(/\D/g, "")
  return digits.length >= 7
}

function hasInviteContact(email: string, phone: string): boolean {
  return isValidInviteEmail(email) || isValidInvitePhone(phone)
}

function isProbablyHeaderRow(row: string[]): boolean {
  if (!row?.length) return false
  const cells = row.map((c) => String(c).toLowerCase().replace(/^\uFEFF/, "").trim())
  if (cells.some((c) => c === "email" || c.endsWith(" email") || c.includes("e-mail"))) return true
  if (cells[0]?.includes("first") && cells[1]?.includes("last")) return true
  return false
}

function layoutFromHeaderRow(row: string[]): ColumnLayout {
  const cells = row.map((c) => String(c).toLowerCase().replace(/^\uFEFF/, "").trim())
  if (cells[2]?.includes("email") || cells[2]?.includes("e-mail")) return "name_first"
  return "email_first"
}

function inferLayoutFromFirstDataRow(row: string[]): ColumnLayout {
  const c0 = (row[0] ?? "").trim().replace(/^\uFEFF/, "").trim()
  const c2 = (row[2] ?? "").trim().replace(/^\uFEFF/, "").trim()
  if (isValidInviteEmail(c0)) return "email_first"
  if (isValidInviteEmail(c2) || isValidInvitePhone(c0)) return "name_first"
  if (isValidInvitePhone(c2)) return "email_first"
  return "email_first"
}

/**
 * Parses CSV or copy-paste text:
 * - Email, First name, Last name, Phone, Notes (optional), or
 * - First name, Last name, Email, Phone, Notes (optional) — with or without a header row.
 * Uses Papa Parse so quoted fields and commas inside notes work like standard CSV exports.
 */
export function parseInviteClientRows(userInput: string, invitationType: string): ParsedInviteClient[] {
  const trimmed = userInput.replace(/^\uFEFF/, "").trim()
  if (!trimmed) return []

  const parsed = Papa.parse<string[]>(trimmed, {
    skipEmptyLines: "greedy",
    transform: (value) => (value == null ? "" : String(value).trim())
  })

  const clients: ParsedInviteClient[] = []
  const rows = parsed.data.filter((r) => r?.length && !r.every((c) => !String(c ?? "").trim()))
  if (!rows.length) return []

  let layout: ColumnLayout = "email_first"
  let start = 0
  if (isProbablyHeaderRow(rows[0]!)) {
    layout = layoutFromHeaderRow(rows[0]!)
    start = 1
  } else {
    layout = inferLayoutFromFirstDataRow(rows[0]!)
  }

  for (let i = start; i < rows.length; i++) {
    const row = rows[i]!
    if (!row?.length) continue
    if (row.every((c) => !String(c).trim())) continue

    let email: string
    let firstName: string
    let lastName: string
    let phone: string
    let notesSlice: string[]

    if (layout === "name_first") {
      firstName = row[0] ?? ""
      lastName = row[1] ?? ""
      email = (row[2] ?? "").trim()
      phone = row[3] ?? ""
      notesSlice = row.slice(4)
    } else {
      email = (row[0] ?? "").trim()
      firstName = row[1] ?? ""
      lastName = row[2] ?? ""
      phone = row[3] ?? ""
      notesSlice = row.slice(4)
    }

    if (email.charCodeAt(0) === 0xfeff) email = email.slice(1).trim()
    if (!hasInviteContact(email, phone)) continue

    const notes = notesSlice.join(",").replace(/^,\s*/, "").trim()

    const client: ParsedInviteClient = {
      email: isValidInviteEmail(email) ? email : "",
      firstName,
      lastName,
      phone: isValidInvitePhone(phone) ? phone : "",
      clientType: "invited",
      invitedFrom: invitationType
    }
    if (notes) client.notes = notes
    clients.push(client)
  }

  return clients
}
